import {
  PageBlockDefinition,
  PageBlockDefinitionsMap,
  PageBlockItemMultiRichTextValue,
  PageBlockItemRichTextValue,
  PageBlockItemTableValue,
  PageBlockText,
  mapPageBlockItemValuesV2,
  removeCommentSpans,
} from "@supernova-studio/model";
import { generateHash } from "../../utils";
import { DocumentationPageEditorModel } from "../docs-editor";

export function generatePageContentHash(
  content: DocumentationPageEditorModel,
  definitions: PageBlockDefinition[],
  debug = false
) {
  let sanitizedContent = structuredClone(content);

  // Remove comment spans
  sanitizedContent = sanitizePageContents(sanitizedContent, definitions);

  // Make sure hashes of empty pages always match
  if (isPageContentEmpty(sanitizedContent)) {
    sanitizedContent = { blocks: [] };
  }

  return generateHash(sanitizedContent, debug);
}

/**
 * Empty page content can be one of the following:
 *   - page without any blocks
 *   - page with a single block, the block is a paragraph whithout any text
 */
function isPageContentEmpty(content: DocumentationPageEditorModel): boolean {
  if (content.blocks.length > 1) return false;
  if (content.blocks.length < 1) return true;

  const singleBlock = content.blocks[0];
  if (singleBlock.type !== "Block" || singleBlock.data.packageId !== "io.supernova.block.rich-text") {
    return false;
  }

  const singleItem = singleBlock.data.items[0];

  // Block has no value, effectively the same as empty value
  if (!singleItem || !singleItem.props["text"]) return true;

  const textValue = singleItem.props["text"] as PageBlockItemRichTextValue;
  return !textValue.value.spans.length;
}

function sanitizePageContents(
  content: DocumentationPageEditorModel,
  definitions: PageBlockDefinition[]
): DocumentationPageEditorModel {
  const defMap = new PageBlockDefinitionsMap(definitions);

  mapPageBlockItemValuesV2(content.blocks, defMap, (block, item, prop, value) => {
    if (prop.type === "RichText") {
      const richTextValue = value as PageBlockItemRichTextValue;
      return {
        ...richTextValue,
        value: sanitizeRichText(richTextValue.value),
      };
    }

    if (prop.type === "MultiRichText") {
      const multiRichTextValue = value as PageBlockItemMultiRichTextValue;
      return {
        ...multiRichTextValue,
        value: multiRichTextValue.value.map(sanitizeRichText),
      };
    }

    if (prop.type === "Table") {
      const tableValue = value as PageBlockItemTableValue;
      tableValue.value.forEach(r => {
        r.cells.forEach(c => {
          c.nodes.forEach(n => {
            if (n.type === "RichText") {
              n.value = sanitizeRichText(n.value);
            }
          });
        });
      });

      return tableValue;
    }

    return value;
  });

  return content;
}

function sanitizeRichText(richText: PageBlockText): PageBlockText {
  return sortSpanAttributes(removeCommentSpans(richText));
}

function sortSpanAttributes(richText: PageBlockText) {
  richText.spans.forEach(s => {
    s.attributes.sort((lhs, rhs) => lhs.type.localeCompare(rhs.type));
  });

  return richText;
}
